import { useCallback, useEffect, useState } from 'react'
import { useCreateTopic, useGetTopicDetails } from '@/hooks/api/dashboard'
import { useLocalStorage } from '@/hooks/common'
import { TopicType } from '@/utils/constants'
import { privacyToAccessType, topicToAlbumDetail } from '../mappers'
import { emptyAlbumXData } from '../utils/albumXData'
import { AlbumDetail, AlbumPrivacy, CreateAlbumInput } from '../types'

export function useAlbumDetail(albumId: string | undefined) {
  const getDetails = useGetTopicDetails()
  const [ewUser] = useLocalStorage('ewUser', null)
  const [album, setAlbum] = useState<AlbumDetail | null>(null)
  const [loading, setLoading] = useState(Boolean(albumId))
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!albumId || albumId.startsWith('new-')) {
        setLoading(false)
        return
      }
      if (!opts?.silent) {
        setLoading(true)
        setError(null)
      }
      try {
        const topic = await getDetails.mutateAsync({ topicID: albumId, pageNo: 1 })
        setAlbum(topicToAlbumDetail(topic, String(ewUser?.userID) === String(topic.userID)))
        setError(null)
      } catch (e) {
        // Keep stale album on background refresh so Lightbox stays mounted
        if (!opts?.silent) {
          setError(e instanceof Error ? e.message : 'Failed to load album')
          setAlbum(null)
        }
      } finally {
        if (!opts?.silent) setLoading(false)
      }
    },
    [albumId, getDetails, ewUser?.userID],
  )

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [albumId, ewUser?.userID])

  return { album, setAlbum, loading, error, refresh }
}

export function useCreateAlbum() {
  const [ewUser] = useLocalStorage('ewUser', null)
  const createTopic = useCreateTopic(ewUser?.userID || '')

  const create = useCallback(
    async (input: CreateAlbumInput) => {
      const result = await createTopic.mutateAsync({
        topicName: input.title.trim(),
        topicType: TopicType.ALBUM.toString(),
        allowContribution: 0,
        topicXData: emptyAlbumXData(input.fullResolutionUploads ?? false, input.allowDownload ?? true),
        privacyType: privacyToAccessType(input.privacy),
        cliques: [],
        connections: [],
      })
      if (result?.isError) {
        throw new Error(result.errorMessage || 'Create failed')
      }
      return result.topicID as string
    },
    [createTopic],
  )

  return { create, isLoading: createTopic.isLoading }
}

export function privacyLabel(p: AlbumPrivacy) {
  if (p === 'public') return 'Public'
  if (p === 'unlisted') return 'Key access'
  return 'Private'
}
